import React, { useRef } from "react";
import Typography from "@mui/material/Typography";
import "react-medium-image-zoom/dist/styles.css";
import Divider from "@mui/material/Divider";
import "../JaldiLab.css";
import {
  generateOrderedList,
  getTextContentHeading,
  h1style,
  referenceHtml,
  referenceTootKit,
  sectionStyle,
  h2style,
  h3style,
  h4style,
  dashedBorderStyle,
  DividerBorderStyle,
  DashedBorderStyle,
  referenceStyle,
} from "./ChapterList";
import objective from "../../../assets/SvgIcons/obj.svg";
import user from "../../../assets/SvgIcons/users.svg";
import adjacencies from "../../../assets/SvgIcons/adjacencies.svg";
import designConsideration from "../../../assets/SvgIcons/design-consideration.svg";
import WAimg1 from "../../../assets/Images/Waiting Area/WAimg1.jpg";
import WAimg2 from "../../../assets/Images/Waiting Area/WAimg2.jpg";
import WAimg3 from "../../../assets/Images/Waiting Area/WAimg3.jpg";
import WAimg4 from "../../../assets/Images/Waiting Area/WAimg4.jpg";
import WAimg5 from "../../../assets/Images/Waiting Area/WAimg5.jpg";
import WAimg6 from "../../../assets/Images/Waiting Area/AccusedWaitingRoom.png";
import CaptionWithBorders1 from "../../../components/ChapterImage/Image";
import generalWaiting2 from "../../../assets/Images/generalWaiting2.jpg";
import generalWaiting3 from "../../../assets/Images/generalWaiting3.jpg";
import waitingAreaImg from "../../../assets/Images/Waiting Area/waitingAreasImg.jpeg";
import {
  FigureDesc41,
  FigureDesc42,
  FigureDesc43,
  ImageDesc51, ImageDesc52
} from "../../../utils/tmp/ImageDescription/WaitingAreasID";

const WaitingAreas = ({ goToSection }) => {
  const referencesRef = useRef(null);

  const handleScrollToReferences = () => {
    if (referencesRef.current) {
      referencesRef.current.scrollIntoView({
        block: "center",
        inline: "nearest",
        behavior: "smooth",
      });
    }
  };

  const reference = (_data, number) => {
    return referenceTootKit(_data, number, handleScrollToReferences);
  };

  const referenceData = [
    {
      content: (
        <>
          'Waiting Areas' <i>(The Courthouse: A Guide to Planning & Design)</i>
        </>
      ),
      link: "https://www.ncsc.org/courthouseplanning/space-planning-standards/waiting-areas",
      description:
        "<https://www.ncsc.org/courthouseplanning/space-planning-standards/waiting-areas>",
    },
    {
      content: (
        <>
          Ar. Prerna Chouhan, ‘Case Study: District Administrative and Judicial
          Court Complex, Sector 76, Mohali’ (<i>Slideshare,</i> 22 January 2021){" "}
        </>
      ),
      link: "https://www.slideshare.net/PrernaChouhan2/case-study-district-administrative-judicial-court-complex-sector-76-mohali",
      description:
        "<https://www.slideshare.net/PrernaChouhan2/case-study-district-administrative-judicial-court-complex-sector-76-mohali> [“Case Study of Mohali District Court”]",
    },
    {
      content: (
        <>
          ‘13 Best Baby Nursing and Changing Rooms in Klang Valley Malls’ (
          <i>Makchic,</i> 1 August 2014)
        </>
      ),
      link: "https://www.makchic.com/9-best-baby-nursing-changing-rooms-in-klang-valley-malls/",
      description:
        " <https://www.makchic.com/9-best-baby-nursing-changing-rooms-in-klang-valley-malls/> [“Makchic”]",
    },
    {
      content: (
        <>
          Judicial Council of California,{" "}
          <i>
            California Trial Court Facilities Standards 2020 (Draft for Public
            Comment)
          </i>
        </>
      ),
      link: "https://www.Courts.ca.gov/documents/facilities-2020-Standards-DRAFT-FOR-COMMENT.pdf",
      description:
        " <https://www.Courts.ca.gov/documents/facilities-2020-Standards-DRAFT-FOR-COMMENT.pdf> p. 4.17.",
    },
  ];

  const waitingRooms = [
    "General waiting rooms",
    "POCSO and Family Court waiting rooms",
    "Spaces for waiting in corridors",
    "Segregated waiting area for the Accused",
    "Waiting area for expert witnesses",
  ];

  const waitingAreaOject = {
    icon: <img src={objective} />,
    heading: "Objectives",
    data: [
      <>
        <span style={{ color: "#DC002D" }}>
          Ensure safe, comfortable, non-intimidating, accessible waiting areas{" "}
        </span>{" "}
        for diverse user groups such as Persons with Disabilities, children, the
        elderly, pregnant women, etc;
      </>,
      <>
        Design{" "}
        <span style={{ color: "#DC002D" }}>
          adequate accessible signage and information systems{" "}
        </span>
        , including adequate IT infrastructure, in the waiting areas to assist
        the parties, Advocates, and general public visiting the Court Complex
      </>,
      <>
        Design all waiting areas to be{" "}
        <span style={{ color: "#DC002D" }}>
          non-confrontational and soothing spaces{" "}
        </span>{" "}
        that make all users feel psychologically safe; and
      </>,
      <>
        <span style={{ color: "#DC002D" }}>Ensure functional efficiency </span>{" "}
        by providing an adequate number of waiting areas on each floor, and for
        each Courtroom or group of Courtrooms, as required.
      </>,
    ],
    isOrdered: false,
    isImage: true,
  };

  const usersObject = {
    icon: <img src={user} />,
    heading: "Users",
    data: [
      "Parties",
      "Advocates",
      "Family members/other visitors connected with the case",
      "General public",
      "Accused Persons",
      "Expert witnesses",
    ],
    isOrdered: false,
    isImage: true,
  };

  const adjacenciesObject = {
    icon: <img src={adjacencies} />,
    heading: "Adjacencies",
    data: [
      "Public waiting areas must be easily accessible from main public entrances.",
      "The waiting areas must be in proximity to stairs, lifts, and elevators to ensure efficient movement.",
      "A lobby area with facilities for waiting must be provided near the main building entrance.",
      "Other general waiting areas must be provided near each Courtroom in the Court Complex. Additionally, seating may be provided in corridors connected to all Courtrooms.",
      "The waiting area for the Accused must be located close to a dedicated separate entrance and exit provided for them.",
      "Dedicated waiting areas must be provided near Special Courts such as POCSO Courts, VWDCs, etc.",
    ],
    isOrdered: false,
    isImage: true,
  };

  const designConsiderationsObject = {
    icon: <img src={designConsideration} />,
    heading: "Design Considerations",
    data: [
      "Adequate multilingual and accessible signages must be provided for easy movement of all persons using the waiting area.",
      "Provisions must be made for accessible case display screens and information kiosks with audible announcements.",
      "Appropriate and separate space, including adequate seating, must be provided for all users as per their requirements.",
      "The design of the waiting area must take into consideration the visual continuity of movement from waiting areas to other spaces.",
      "Adequate provision must be made for amenities, including sanitary provisions such as accessible toilets and drinking water facilities, and a play area to ensure that waiting areas are child-friendly and cater to instances where persons may be visiting the Court with their children.",

      <>
        The waiting areas must be in close proximity or have direct access to a
        Quiet Room and a Feeding to cater to the needs of diverse users, when
        necessary. For further details of the design considerations for a Quiet
        Room, please refer to{" "}
        <span
          style={sectionStyle}
          onClick={() => goToSection("quietSensoryRoom", "HeadingQuietRoom")}
        >
          {" "}
          Section 3.6.
        </span>
        , and for Feeding Room, please refer to{" "}
        <span
          style={sectionStyle}
          onClick={() =>
            goToSection("familyChildFriendlyServices", "feedingArea")
          }
        >
          {" "}
          Section 3.3.B.
        </span>
      </>,
      "The design of all waiting areas must ensure minimal noise transmission from the waiting areas to the Courtrooms or other public-facing administrative offices.",

      <>
        The design must include sufficient acoustically absorptive materials
        (preferably in the ceiling) as well as acoustic isolation, to avoid
        noise buildup and disturbances in the waiting area. For further details
        on acoustics, please refer to{" "}
        <span
          style={sectionStyle}
          onClick={() => goToSection("annexes", "HeadingAnnexure5")}
        >
          {" "}
          Annexure 5.
        </span>
      </>,

      <>
        Adequate amenities such as canteen facilities, as well as basic
        first-aid services,{reference(referenceData[0], 1)} must be provided in
        convenient locations in or near all waiting areas.
      </>,
      "All waiting areas must have less obtrusive and non-intimidating security provisions.",
      "Provision must be made for appropriate fire safety provisions, including emergency evacuation plans, as per norms.",
      "Adequate CCTV surveillance equipment must be provided in all waiting areas.",
    ],
    isOrdered: false,
    isImage: true,
  };

  const POCOSObject = {
    icon: <img src={designConsideration} />,
    heading: "Specific Design Considerations",
    data: [
      <>
        In addition to the design considerations laid out in the section above,
        the waiting rooms specifically designed for POCSO Courts and Family
        Courts should also follow child-friendly design guidelines. For further
        details, see{" "}
        <span
          onClick={() => goToSection("courtroom", "special-courtrooms")}
          style={sectionStyle}
        >
          Sections 2.1.B.{" "}
        </span>{" "}
        and{" "}
        <span
          onClick={() => goToSection("vwdc", "vulnerable-witness")}
          style={sectionStyle}
        >
          2.3.A.{" "}
        </span>
      </>,
      "Additionally, these waiting rooms must be in close proximity, or have direct access to, family-friendly sanitary provisions.",

      <>
        These spaces must be acoustically treated to avoid noise transfer from
        adjacent areas and vice versa. Sufficient acoustically absorptive
        materials must be provided to avoid reverberation/echo. For further
        details on acoustics, please refer to{" "}
        <span
          style={sectionStyle}
          onClick={() => goToSection("annexes", "HeadingAnnexure5")}
        >
          {" "}
          Annexure 5
        </span>
        .
      </>,

      "Silent duress alarm buttons and video surveillance, with camera coverage including the interior and exterior corridors by the door as well as the waiting area, must be provided.",
      <>
        A door intercom, remote door release, and local alarm system must be
        provided to notify staff that a child has entered an out-of-view area,
        or that someone has entered the waiting area without permission.
        {reference(referenceData[3], 4)}
      </>,
    ],
    isOrdered: false,
    isImage: true,
  };
  const corridorWaitingObject = {
    icon: <img src={designConsideration} />,
    heading: "Specific Design Considerations",
    data: [
      "Waiting areas in corridors must have comfortable, ergonomic seating including accessible seating provisions.",
      "Case display screens outside the Courtrooms, and auditory announcements regarding the cases being heard, must be provided.",
      "These waiting areas must be in close proximity to accessible sanitary provisions, including washrooms and drinking water.",
    ],
    isOrdered: false,
    isImage: true,
  };

  const accusedWaitingObject = {
    icon: <img src={designConsideration} />,
    heading: "Specific Design Considerations",
    data: [
      <>
        These waiting rooms must be located along the segregated paths with
        controlled access points to be followed by the Accused for movement
        through the Court Complex, and must be accessible only by the Accused
        and accompanying police personnel. For further details, please refer to{" "}
        <span
          onClick={() => goToSection("mappingUserJourneys", "JudicialStaff")}
          style={sectionStyle}
        >
          Sections 1.4{" "}
        </span>{" "}
        and{" "}
        <span
          onClick={() => goToSection("mappingUserJourneys", "Litigants")}
          style={sectionStyle}
        >
          1.5{" "}
        </span>
      </>,

      "These specific waiting rooms may be of a smaller size to accommodate fewer users with grills to ensure visibility within the waiting area for the accompanying police officers. ",

      "For such instances where the Accused may have to be placed in the Accused waiting area during meal times, specific provisions should be made to provide them with food in a systematic and hygienic manner.",

      // "The waiting area must be designed to have controlled access points and segregated pathways, to minimize the interaction of accused with other Court users and maintain privacy.",

      "The waiting area design must be in compliance with accessibility standards to accommodate Persons with Disabilities, including sensory and cognitive disabilities.",
      "Inclusive signage and communication systems must be incorporated to ensure adequate guidance and dissemination of information within the waiting area.",
      "Additional security provisions, including secure entrances, comprehensive CCTV surveillance cameras, and rigorous screenings, must be provided.",
      "The security features provided must be subtle to maintain safety without compromising comfort.",

      <>
        The waiting areas must be acoustically treated to reduce noise build-up
        and interference with adjacent areas. For further details on acoustics,
        please refer to{" "}
        <span
          style={sectionStyle}
          onClick={() => goToSection("annexes", "HeadingAnnexure5")}
        >
          {" "}
          Annexure 5
        </span>
        .
      </>,

      "Specific emergency protocols and communication systems must be incorporated within the Accused waiting area for quick responses to unforeseen situations.",

      "The waiting areas on each floor should be segregated to accommodate Accused Persons who may be of different genders, with specific waiting areas being provided for juveniles. ",
    ],
    isOrdered: false,
    isImage: true,
  };

  const expertWaitingObject = {
    icon: <img src={designConsideration} />,
    heading: "Specific Design Considerations",
    data: [
      "These waiting rooms may be of a smaller size than general waiting areas, to accommodate the fewer number of users.",
      "The waiting area design must be in compliance with accessibility standards to accommodate Persons with Disabilities, including sensory and cognitive disabilities.",
      "Inclusive signage and communication systems must be incorporated to ensure adequate guidance and dissemination of information within the waiting area.",
      "Case display screens, and auditory announcements regarding case status for the connected Courtrooms, must be provided.",
      <>
        The waiting area must be designed to have designated private or
        semi-private areas with adequate acoustic treatment within the waiting
        room to allow confidential discussions between experts and Advocates.
        For further details on acoustics, please refer to{" "}
        <span
          style={sectionStyle}
          onClick={() => goToSection("annexes", "HeadingAnnexure5")}
        >
          {" "}
          Annexure 5
        </span>
        .
      </>,

      "The waiting area must have tables or workstations equipped with power outlets and adequate lighting for experts to review case materials or prepare documents.",
      "Security measures must be implemented to safeguard sensitive information discussed or handled by expert witnesses while they wait.",
    ],
    isOrdered: false,
    isImage: true,
  };

  const genreralWaitingArea = {
    icon: <img src={designConsideration} />,
    heading: "Specific Design Considerations",
    data: [
      <>
        General waiting rooms must be provided on each floor of the Court
        Complex,{reference(referenceData[1], 2)} and for each Courtroom or group
        of Courtrooms, depending on the footfall and the number of cases being
        listed in the Court on a day-to-day basis.
      </>,
      <>
        General waiting rooms must be provided with facilities catering to
        diverse user groups, including information kiosks, nursing rooms,
        {reference(referenceData[2], 3)} quiet rooms, prayer rooms,
        child-friendly facilities such as a play area, etc.
      </>,
      "Additionally, the waiting rooms must be equipped with ergonomic seating, adequate natural light and ventilation, access to green spaces or indoor plants (as required), etc.",
      "Adequate seating facilities, including designated space for wheelchairs and prams, must be provided in all waiting areas.",
      "Case display screens, and auditory announcements regarding case status for the connected Courtrooms, must be provided in all waiting areas.",
      "Amenities including sanitary provisions, vending machines, etc., must be provided.",
      <>
        The space must be acoustically treated with acoustically absorptive
        ceiling materials, to avoid noise buildup. For further details on
        acoustics, please refer to{" "}
        <span
          style={sectionStyle}
          onClick={() => goToSection("annexes", "HeadingAnnexure5")}
        >
          {" "}
          Annexure 5.
        </span>
      </>,
    ],
    isOrdered: false,
    isImage: true,
  };

  const paraStyle = {
    fontFamily: "Lato",
    fontSize: "18px",
    fontWeight: "400",
    lineHeight: "27px",
    color: "#555555",
    letterSpacing: "0em",
    textAlign: "justified",
  };

  return (
    <div>
      <Typography>
        <h1 id="handbookPrinciple" style={h1style}>
          PART C - THE HANDBOOK
        </h1>{" "}
        <h2 style={h2style} id="courtComplex">
          <span style={{ fontSize: "24px" }}>2. </span>
          ORGANIZATION AND DESIGN OF SPACES OF THE COURT COMPLEX
        </h2>
        <h3 id="headingWaitingAreas" style={h3style}>
          <span style={{ fontSize: "20px" }}>2.2. </span>
          Waiting Areas
        </h3>
        <p style={paraStyle}>
          In order to ensure a comfortable, safe, secure and user-friendly
          environment for all visitors to the Court Complex, sufficient spaces
          must be provided for well-lit and well-equipped waiting areas at
          various spaces throughout the Court Complex. Sufficient number of
          waiting areas should particularly be located near Courtrooms and other
          public-facing administrative offices. All waiting areas must be
          adequately equipped to serve the needs of diverse user groups.
        </p>
        <p style={paraStyle}>Five types of waiting areas must be provided:</p>
        {generateOrderedList(waitingRooms)}
        {getTextContentHeading(waitingAreaOject)}
        {getTextContentHeading(usersObject)}
        {getTextContentHeading(adjacenciesObject)}
        {getTextContentHeading(designConsiderationsObject)}
        <DividerBorderStyle />
        <h4 id="generalWaiting" style={h3style}>
          <span style={{ fontSize: "20px" }}>2.2.</span>A. General Waiting Rooms
        </h4>
        <p style={paraStyle}>
          The general waiting rooms are provided throughout the Court Complex
          for parties and Advocates to wait for their cases to be called for
          hearing, and for the use of the general public visiting the Court
          Complex.
        </p>
        <CaptionWithBorders1
          image={[waitingAreaImg]}
          caption="Image 51: Examples of accessible seating in waiting areas"
          creditLabel="Image Credit"
          imageCredit="369 Ochre Studio"
          zoomImgStyle={{ height: "500px" }}
          imageDescription={ImageDesc51}
        />
        {getTextContentHeading(genreralWaitingArea)}
        <CaptionWithBorders1
          image={[WAimg4]}
          isZoom={true}
          caption="Figure 41: Typical floor plan for a general waiting area"
          zoomImgStyle={{ height: "550px" }}
          imageDescription={FigureDesc41}
        />
        
        <DividerBorderStyle />
        <h4 id="familyWaiting" style={h3style}>
          <span style={{ fontSize: "20px" }}>2.2.</span>B. POCSO and Family
          Court Waiting Areas
        </h4>
        <p style={paraStyle}>
          Separate waiting areas must additionally be provided near POCSO Courts
          and Family Courts.{" "}
        </p>
        {getTextContentHeading(POCOSObject)}
        <CaptionWithBorders1
          image={[generalWaiting2]}
          zoomImgStyle={{ height: "500px" }}
        />
        <CaptionWithBorders1
          image={[generalWaiting3]}
          zoomImgStyle={{ height: "400px" }}
        />
        <CaptionWithBorders1
          image={[]}
          caption="Image 52: Examples of Feeding Rooms in Waiting Areas"
          creditLabel="Source"
          imageCredit="Makchic"
          zoomImgStyle={{ height: "500px" }}
          imageDescription={ImageDesc52}
        />
        <DividerBorderStyle />
        <h4 id="corridorWaiting" style={h3style}>
          <span style={{ fontSize: "20px" }}>2.2.</span>C. Corridor Waiting
          Areas
        </h4>
        <p style={paraStyle}>
          Sufficient seating arrangements must be provided in the corridors
          outside Courtrooms, for parties and Advocates awaiting their cases to
          be called for hearing.{" "}
        </p>
        {getTextContentHeading(corridorWaitingObject)}
        <CaptionWithBorders1
          image={[WAimg5]}
          isZoom={true}
          caption="Figure 42: Typical floor plan for waiting areas in corridors"
          zoomImgStyle={{ height: "500px" }}
          imageDescription={FigureDesc42}
        />
        <DividerBorderStyle />
        <h4 id="accusedWaiting" style={h3style}>
          <span style={{ fontSize: "20px" }}>2.2.</span>D. Waiting Areas for
          Accused Persons
        </h4>
        <p style={paraStyle}>
          A separate waiting area for the Accused Persons must be strategically
          provided near Courtrooms, to wait for their cases to be called for
          hearing.
        </p>{" "}
        {getTextContentHeading(accusedWaitingObject)}
        <CaptionWithBorders1
          image={[WAimg6]}
          isZoom={true}
          caption="Figure 43: Typical floor plan for an Accused waiting room"
          zoomImgStyle={{ height: "500px" }}
          imageDescription={FigureDesc43}
        />
        <DividerBorderStyle />
        <h4 id="expertWaiting" style={h3style}>
          <span style={{ fontSize: "20px" }}>2.2.</span>E. Waiting Areas for
          Expert Witnesses
        </h4>
        <p style={paraStyle}>
          A separate waiting area must be provided near Courtrooms for expert
          witnesses who have been called to provide evidence or testimony in
          cases, to ensure that the general waiting areas are not overcrowded
          and witnesses have adequate space and access to facilities.
        </p>
        {getTextContentHeading(expertWaitingObject)}
        <h2 ref={referencesRef} id="waitingRef" style={referenceStyle}>
          References
          <Divider />
        </h2>
        {referenceHtml(referenceData)}
      </Typography>
    </div>
  );
};

export default WaitingAreas;
